import React from 'react';
import { useLocation } from "react-router-dom";
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import Button from '@mui/material/Button';
import { TextField, Tooltip, Typography } from '@mui/material';

import { Item, Item2 } from '../../styles';
import DropPanel from '../../components/DropPanel';
import { getPreview } from '../../services/Spectrum';

import Header from '../../components/HeaderComponent';
import ErrorDialog from '../../components/ErrorDialog';
import RefreshError from '../../schemas/Exception/RefreshError';
import InternalError from '../../schemas/Exception/InternalError';
import UnusualError from "../../schemas/Exception/UnusualError";
import { fetchGet, fetchPost } from '../../services/GettingData';
import HelpIconWithHint from '../../components/HelpIconWithHint';
import { SearchDirection } from '../../schemas/Spectrum/SearchDirection';
import ISpectrumPreviewModel from '../../schemas/Spectrum/ISpectrumPreviewModel';
import NotAuthorizedError from '../../schemas/Exception/NotAuthorizedError';

import '../../App.css';

type CompoundSearchProps = {
    spectrumPreview: ISpectrumPreviewModel
};

export const PredictCompoundSearch: React.FC<CompoundSearchProps> = (props) => {
    const [isDialogOpened, setDialogOpened] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [showMessage, setShowMessage] = React.useState<string>('');
    const spectrumSearchButtonRef = React.useRef<HTMLButtonElement>(null);
    const [files, setFiles] = React.useState([]);

    const [spectrumSearch, setSpectrumSearch] = React.useState<ISpectrumPreviewModel>({
        peaks: (props as any).location.state?.spectrumPreview?.searchDirection === SearchDirection.ByPeaks ?
            (props as any).location.state?.spectrumPreview?.peaks.sort((a: number, b: number) => a - b) : [],
        spectrum_type: (props as any).location.state?.spectrumPreview?.spectrum_type,
        solvent: undefined,
        is_complete: false,
        graph: '',
        spectrumString: (props as any).location.state?.spectrumPreview?.searchDirection === SearchDirection.BySpectrumString ?
            (props as any).location.state?.spectrumPreview?.spectrumString : '',
        structure: '',

    });


    const closeErrorDialog = () => setDialogOpened(false);

    const showError = (errorMessage: string) => {
        setErrorMessage(errorMessage);
        setDialogOpened(true);
    }


    const handleChange = (files: File[]) => {
        setFiles(files);
    };


    React.useEffect(() => {
        if (spectrumSearch.graph) {
            (props as any).history.push({
                pathname: '/predict-compound-preview',
                state: { spectrumPreview: spectrumSearch }
            });
        }
    }, [spectrumSearch]);



    const submitSearch = async () => {
        let response: any;
        try {
            if (files.length > 0) {
                response = await searchByZip() as ISpectrumPreviewModel;
                response.searchDirection = SearchDirection.ByZipFile;
            }
            else
                if (spectrumSearch.spectrumString) {
                    response = await searchByString() as ISpectrumPreviewModel;
                    response.searchDirection = SearchDirection.BySpectrumString;
                }
                else
                    if (spectrumSearch.peaks.length > 0) {
                        if (!spectrumSearch.spectrum_type) {
                            setDialogOpened(true);
                            setShowMessage('Please, select Spectrum Type');
                            return;
                        }
                        response = await searchByPeaks() as ISpectrumPreviewModel;
                        response.searchDirection = SearchDirection.ByPeaks;
                    }

            if (response) {
                setSpectrumSearch(prev => ({
                    ...response as any,
                    spectrumString: prev.spectrumString,
                    peaks: response.peaks.sort((a: number, b: number) => a - b),

                }))

            }
            else {
                setDialogOpened(true);
                setShowMessage('Please, input some data');

            }
        }
        catch (e: any) {
            if (e instanceof RefreshError) {
                showError(e.message);
                (props as any).history.push({ pathname: '/login', state: { backTo: '/predict-spectrum-search', welcomeMessage: true } })
                return;
            }
            if (e instanceof NotAuthorizedError) {
                (props as any).history.push({ pathname: '/login', state: { backTo: '/predict-spectrum-search', welcomeMessage: true } })
                return;
            }
            if (e instanceof RefreshError) {
                showError(e.message);
                (props as any).history.push({ pathname: '/login', state: { backTo: '/predict-spectrum-search', welcomeMessage: true } })
            }
            if (e instanceof UnusualError) {
                showError('File is not supported');
                return;
            }
            if (e instanceof InternalError) {
                showError('Incorrect spectrum string');
                return;
            }
            console.error(e);
            showError(e);
        }
    }

    const handleSubmitAndNavigate = async () => {
        try {
            await submitSearch();

        } catch (error) {
            console.error("Error during submission:", error);
        }
    };


    const rows = [];
    for (let i = 0; i < spectrumSearch.peaks.length; i++) {
        rows.push({ id: i, shift: spectrumSearch.peaks[i] });
    }



    const searchByZip = async (): Promise<ISpectrumPreviewModel> => {
        const defaultErrorMessage = 'Incorrect uploaded file';
        const dataTypeDx = 'multipart/form-data';
        const dataTypeZip = 'application/x-zip-compressed';

        let endPoint = '/spectrum/preview/upload';
        let file = files[0];
        let body = new FormData()
        let isDx = file.name.toLowerCase().endsWith('.dx');
        if (isDx) {
            let blobWithNewType = new Blob([file], { type: "chemical/x-jcamp-dx" });
            body.append("file", blobWithNewType, file.name);
        }
        else
            body.append('file', file);
        const response = await fetchPost(endPoint, body, true, true, isDx ? dataTypeDx : dataTypeZip, defaultErrorMessage);
        if (!response.ok) {
            if (response.status === 501) {
                setDialogOpened(true);
                setShowMessage('Incorrect NMR file');
                return;
            }
            setDialogOpened(true);
            setShowMessage('Incorrect uploaded file');
            return;
        }
        return await response.json();
    }

    const searchByPeaks = async (): Promise<ISpectrumPreviewModel> => {
        const response = await getPreview({
            'spectrum_type': spectrumSearch.spectrum_type,
            "peaks": spectrumSearch.peaks,
            "solvent": spectrumSearch.solvent === '' ? undefined : spectrumSearch.solvent,
            "is_complete": false,
            "spectrumString": "",
            "structure": "",
        });
        return response;
    }

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                console.log('kd enter', spectrumSearch.is_complete);

                if (!spectrumSearch.is_complete)
                    submitSearch();
                else
                    spectrumSearchButtonRef.current.click();
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [spectrumSearch.is_complete]);


    const searchByString = async (): Promise<ISpectrumPreviewModel> => {
        const response = await fetchGet('/spectrum/preview/string?spectrum_string=' + spectrumSearch.spectrumString, true, true, 3);
        if (!response.ok)
            throw 'Incorrect spectrum string,\r\n' + response.statusText;
        return await response.json();
    }



    return (
        <Grid container md={12} spacing={0} className='main-frame'>
            <Grid container md={12}>
                <Item2><div style={{ height: '2em' }}></div></Item2>
            </Grid>

            <ErrorDialog isDialogOpened={isDialogOpened}
                errorMessage={errorMessage}
                warningMessage={showMessage}
                onClose={closeErrorDialog} />

            <Grid xs={12}>
                <Header title={'NMR predictions'}
                    helpAddress='help#htuss'
                    showLogin={true} />
            </Grid>

            <Grid container xs={12} spacing={0} style={{ height: '5em' }}>
            </Grid>

            <Grid container xs={12} spacing={0} style={{ marginTop: '.5em' }}>
                <Grid xs={8}>
                    <Typography sx={{
                        fontSize: '1.5em',
                        fontWeight: 'bold',
                        color: '#000000',
                        textAlign: 'left',
                        marginLeft: '1em'
                    }}>
                        Predict structure from NMR spectrum
                    </Typography>
                </Grid>
                <Grid xs={8}>
                    <Typography sx={{
                        fontSize: '1.5em',
                        fontWeight: 'bold',
                        color: '#000000',
                        textAlign: 'left',
                        marginLeft: '1em'
                    }}>
                        Works only for 13C NMR of PURE compound in CDCl3
                    </Typography>


                </Grid>
            </Grid>

            <Grid container md={12} spacing={0} style={{ marginTop: '2em' }}>
                <Grid container
                    style={{ marginBottom: '2em' }}
                    spacing={0}
                    md={12}
                    xs={12}
                    direction="column"
                    alignItems="center"
                    justifyContent="center">

                    <Item style={{
                        width: '100%',
                        minHeight: '6em',
                        backgroundColor: '#d5f5f5',
                        textAlign: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <DropPanel onChange={handleChange}
                            text={'Drag and drop the file with your spectrum here.'}
                            warningOnly=''
                            warningSupport={"For NMR data: ZIP archive with spectrum folder.\nBruker and Varian files are supported. 1r file should be in the Bruker file folder\nOnly normal quality spectra are will be correctly processed (S/N > 10/1, flat baseline).\nFor IR data: .dx file with spectrum"} />
                        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} className='mol-container'></div>
                    </Item>

                    <Item style={{ width: '100%', marginTop: '1em' }}>
                        <div className='mol-container'>
                            <TextField className='spectrum-text'
                                type="text"
                                value={spectrumSearch.spectrumString}
                                sx={{
                                    input: {
                                        "&::placeholder": {
                                            opacity: .5,
                                        },
                                    },
                                    label: { color: 'blue' }
                                }}
                                onChange={(c) => {
                                    setSpectrumSearch(prev => ({ ...prev, spectrumString: c.target.value }))
                                }}
                                style={{ width: '90%' }}
                                placeholder='Paste spectrum string here' />
                            <div style={{
                                marginTop: '4px',
                                cursor: 'pointer',
                                color: 'rgba(0, 0, 0, 0.6)',
                                fontSize: '0.75rem',
                                textAlign: 'left',
                                width: '90%',
                                marginLeft: 'auto',
                                marginRight: 'auto'
                            }}
                                onClick={() => {
                                    setSpectrumSearch(prev => ({
                                        ...prev,
                                        spectrumString: "13C NMR (100 MHz, CDCl3) δ 19.5, 32.5, 46.5, 120.5, 126.3, 126.4, 126.8, 134.2, 149.1, 159.5, 161.0",
                                    }))
                                }}>
                                {'ex. 13C NMR (100 MHz, CDCl3) δ 19.5, 32.5, 46.5, 120.5, 126.3, 126.4, 126.8, 134.2, 149.1, 159.5, 161.0'}
                            </div>
                        </div>
                    </Item>



                </Grid>
            </Grid>

            <Grid container md={12} spacing={2} style={{ marginTop: '0em' }}>
            </Grid>

            <Grid container md={12} spacing={0} style={{ marginTop: '2em' }}>
                <Grid md={4} display={{ xs: 'none', md: 'block' }}></Grid>
                <Grid md={4} xs={12} style={{ minWidth: '220px' }}>
                    <Item2 style={{ fontSize: '2em', paddingTop: '0px', paddingBottom: '0px' }}>
                    </Item2>
                </Grid>
                <Grid md={4} xs={12} className='pamd1'>
                    <Item2 className="responsive-submit-container" style={{ fontSize: '2em', paddingTop: '0px', paddingBottom: '0px' }}>
                        {<div>
                            <Tooltip arrow title={'Predict structure using AI'}>
                                <Button sx={{
                                    "&.MuiButton-contained": {
                                        backgroundColor: "success.main"
                                    },
                                    "&.MuiButton-contained:hover": {
                                        backgroundColor: "success.dark"
                                    },
                                    "&.MuiButton-contained:focus": {
                                        backgroundColor: "success.main"
                                    },
                                    "&.MuiButton-contained.Mui-focusVisible": {
                                        backgroundColor: "success.main",
                                        boxShadow: "none"
                                    },
                                    "&:focus": {
                                        outline: "none",
                                        backgroundColor: "success.main"
                                    },
                                    "&.Mui-focused": {
                                        backgroundColor: "success.main"
                                    }
                                }}
                                    ref={spectrumSearchButtonRef}
                                    color="success"
                                    variant="contained"
                                    onClick={handleSubmitAndNavigate}>
                                    {'Submit'}
                                </Button>
                            </Tooltip>
                            <HelpIconWithHint title={''}
                                text={'Use AI to predict molecular structure from your NMR data'}
                                handleOpen={() => { }} />
                        </div>
                        }
                    </Item2>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default PredictCompoundSearch;